import {
  useModal,
  useRoute,
  useRouter,
  useTranslation,
} from '@/composables/plugins';
import { useAdminSession } from '@/composables/useAdminSession';
import { computed, reactive, ref, watchEffect } from 'vue';
import { DEFAULT_ORDER_SAVE_PROPS, useOrderStore } from '@/stores/order';
import { track } from '@/helpers/tracker';
import { useOrderUI } from '@/stores/orderUi';
import type { NavigationItem } from '@/components/elements/Order/navigation';
import {
  navigation as navigationItems,
  isOrderTabGreaterThan,
} from '@/components/elements/Order/navigation';
import { useActiveUserStore } from '@/stores/user';
import { orderStatusGreaterOrEqual } from '@/helpers/order-helpers';
import { useOrderFiles } from '@/stores/orderFiles';
import { useConversationsStore } from '@/stores/conversations';
import { useOrderAuthentication } from './useOrderAuthentication';
import { useFlag } from '@/composables/useFlag';
import { NEW_ISSUES_TAB_FLAG } from '@cutr/constants/order-issues';
import { useFilesManagement } from '@/composables/useFilesManagement';
import { usePricingChanges } from '@/stores/pricingChanges';
import type { AxiosResponse } from 'axios';
import { useFormKitContextById, submitForm } from '@formkit/vue';
import type { ToBeFixed } from '@/vite-env';
import { storeToRefs } from 'pinia';

const DEFAULT_VALIDATION_STATE = {
  files: false,
  options: true,
  delivery: false,
  materials: true,
  cutlist: false,
  review: false,
};

const lastValidStep = ref('');
const tncAccepted = ref(false);
const orderSuccess = ref(false);
const stepSubmitted = ref(false);
const stepValidation: Record<NavigationItem['name'], boolean> = reactive({
  ...DEFAULT_VALIDATION_STATE,
});

function reset() {
  orderSuccess.value = false;
  tncAccepted.value = false;
  stepSubmitted.value = false;
  lastValidStep.value = '';
  Object.keys(DEFAULT_VALIDATION_STATE).forEach((key) => {
    stepValidation[key] =
      DEFAULT_VALIDATION_STATE[key as keyof typeof DEFAULT_VALIDATION_STATE];
  });
}

export const useOrderNavigation = () => {
  const authController = useOrderAuthentication();
  const modal = useModal();
  const router = useRouter();
  const route = useRoute();
  const userStore = useActiveUserStore();
  const { isExternalAdmin } = useAdminSession();
  const orderUIStore = useOrderUI();
  const orderStore = useOrderStore();
  const conversationsStore = useConversationsStore();
  const { i18next } = useTranslation();
  const filesStore = useOrderFiles();
  const hasPendingPricingChanges = userStore.isCustomer
    ? storeToRefs(usePricingChanges()).hasPendingPricingChanges
    : computed(() => false);
  const navT = i18next.getFixedT(null, 'order', 'navigation');
  const { hasFilesManagement, isPositionsOrder } = useFilesManagement();
  const isNewIssuesTabEnabled = useFlag(NEW_ISSUES_TAB_FLAG);

  const lastValidStepStorageKey = computed(
    () => `lastValidStep-${orderStore.orderId}`
  );

  const nextButtonActive = computed(() =>
    Boolean(
      stepValidation[currentStep.value as keyof typeof stepValidation] ?? true
    )
  );

  const hasManufacturingIssues = computed(() =>
    filesStore.files.some((file) => file.cadFileManufacturingIssues?.length)
  );

  const isPaneExpanded = computed(
    () => currentTabConfig.value?.paneExpanded && !orderUIStore.isViewerActive
  );

  const currentStep = computed({
    get() {
      return getSubRouteName(route) || '';
    },
    set(val: string) {
      updateStepURL(val);
      orderUIStore.toggle('isViewerActive', val === '');

      if (!orderStore.customerIsEditing) return;
      if (val === '') return; // when toggling the tab
      lastValidStep.value = isOrderTabGreaterThan(val, lastValidStep.value)
        ? val
        : lastValidStep.value;
      localStorage.setItem(lastValidStepStorageKey.value, lastValidStep.value);
    },
  });

  const currentTabs = computed(() => navigation.value.map((t) => t.name));
  const currentTabConfig = computed(() =>
    navigation.value.find((t) => t.name === currentStep.value)
  );

  const showNextButton = computed(
    () => orderStore.isDraft && !userStore.isAdmin
  );

  const showCheckout = computed(() => {
    if (!orderStore.isDraft) return false;
    if (userStore.isAdmin && currentStep.value === 'project') return true;
    return (
      orderStore.customerIsEditing && ['review'].includes(currentStep.value)
    );
  });

  const hasDeliveryAddress = computed(() =>
    Boolean(orderStore.currentOrder.customerFulfilmentAddressId)
  );

  const checkoutActive = computed(
    () =>
      showCheckout.value &&
      nextButtonActive.value &&
      tncAccepted.value &&
      hasDeliveryAddress.value
  );

  const currfentFormState = computed(() => {
    const formName = currentTabConfig.value?.formName;
    if (!formName) return null;
    return useFormKitContextById(formName).value?.state;
  });

  const hasOwnViewer = (step: string) =>
    [
      'material-assignment',
      'materials',
      'quote-file',
      'positions',
      !isPositionsOrder.value && hasFilesManagement.value && 'files',
    ]
      .filter(Boolean)
      .includes(step);

  // Positions deprecates a file always being selected
  const autoSelectsDefaultFile = (step: string) => step !== 'positions';

  const navigation = computed(() => {
    // dynamically enable tabs
    const enableOverrides: Record<string, () => boolean> = {
      pricing: () => userStore.isAdmin,
      positions: () => isPositionsOrder.value,
      files: () => !isPositionsOrder.value,
      options: () => !userStore.isMaker && !isPositionsOrder.value,
      materials: () => {
        if (userStore.isAdmin) {
          return false;
        }

        if (userStore.isMaker) {
          return orderStatusGreaterOrEqual(
            orderStore.currentOrder,
            'quote_ready'
          );
        }

        return true;
      },
      review: () => orderStore.customerIsEditing,
      project: () => userStore.isAdmin || !orderStore.isDraft,
      feedback: () => !orderStore.isDraft && hasManufacturingIssues.value,
      cutlist: () =>
        orderStore.isCutlist &&
        (userStore.isAdmin || orderStore.customerIsEditing),
      messages: () =>
        conversationsStore.hasConversations &&
        (userStore.isAdmin || !orderStore.isDraft),
      issues: () => isNewIssuesTabEnabled.value,
      quotes: () => {
        if (userStore.isAdmin && isExternalAdmin.value) return false;
        if (userStore.isAdmin) return true;
        if (orderStore.isDraft) return false;
        if (userStore.isCustomer) return true;
        if (userStore.isMaker) return userStore.activeSessionRole === 'owner';
        return false;
      },
      billing: () => {
        if (userStore.isAdmin && isExternalAdmin.value) return false;
        if (userStore.isAdmin) return true;

        if (userStore.isCustomer) {
          return orderStatusGreaterOrEqual(
            orderStore.currentOrder,
            'quote_request'
          );
        }

        if (userStore.isMaker) {
          return (
            userStore.activeSessionRole === 'owner' &&
            orderStore.currentOrder.makerId === userStore.organisationId
          );
        }

        return false;
      },
      timeline: () => {
        if (userStore.isAdmin && isExternalAdmin.value) return false;
        if (userStore.isAdmin) return true;

        return false;
      },
    };

    const labelOverrides: Record<string, () => string> = {
      delivery: () => 'fulfilment',
      'material-assignment': () => 'materials',
      quotes: () => (userStore.isMaker ? 'offers' : 'quotes'),
    };

    // show a notifications icon
    const shouldNotify = (name: string) => {
      if (!['quotes', 'messages'].includes(name)) return 0;

      if (name === 'messages') {
        return conversationsStore.unreadCount;
      }

      if (orderStore.currentOrder?.hasReadyQuotes) return 1;

      if (userStore.isCustomer && hasPendingPricingChanges.value) return 1;

      if (userStore.isMaker) {
        return orderStore.currentOrder?.makerOffers?.filter(
          (o) => o.status === 'dispatched'
        ).length;
      }

      return 0;
    };

    // override the pane expanded state of certain tabs
    const paneExpandedOverrides: Record<string, boolean> = {
      quotes: ['Customer', 'Maker'].includes(userStore.activeSessionType),
      files: hasFilesManagement.value,
    };

    if (orderStore.isCutlist) {
      enableOverrides.files = () => false;
      enableOverrides.options = () => false;
      enableOverrides.materials = () => false;
      enableOverrides.project = () => userStore.isAdmin || !orderStore.isDraft;
    }

    const fullOverrides: Record<
      string,
      (nav: NavigationItem) => NavigationItem
    > = {
      files: (nav) => ({
        ...nav,
        intake: !hasFilesManagement.value,
        paneExpanded: hasFilesManagement.value,
        formName: hasFilesManagement.value ? undefined : nav.formName,
      }),
    };

    return navigationItems
      .map((n) => ({
        ...n,
        notify: shouldNotify(n.name),
        errorKey: n.errorKey || n.name,
        paneExpanded: paneExpandedOverrides[n.name] || n.paneExpanded,
        enabled: enableOverrides[n.name] ? enableOverrides[n.name]() : true,
        label: navT(labelOverrides[n.name] ? labelOverrides[n.name]() : n.name),
      }))
      .map((n) => fullOverrides[n.name]?.(n) ?? n)
      .filter((t) => (userStore.isAdmin || !t.admin) && Boolean(t.enabled));
  });

  const userNavigation = computed(() =>
    navigation.value.filter((n) => n.hideFromMenu !== true)
  );

  function trackCurrentTab(tab?: string) {
    track('order:tab-view', {
      tab: tab || currentStep.value,
      order: orderStore.currentOrder,
    });
  }

  function updateStepURL(
    val?: string,
    query?: Record<string, string | undefined>
  ) {
    const hasQuery = Boolean(Object.keys(query || {}).length);
    const subRoute = val ? `:${val}` : '';
    const action = currentStep.value && !hasQuery ? 'push' : 'replace';
    orderUIStore.toggle('isNavOpen', false);

    return router[action]({
      name: `order${subRoute}`,
      query: { ...route.query, ...query },
    });
  }

  function goToStep(navigatedTo: string, direction = 'next') {
    currentStep.value = navigatedTo;
    stepSubmitted.value = false;
    const orderId = orderStore.orderId;
    track('order:intake-direction', {
      order: orderStore.currentOrder,
      orderId,
      direction,
      navigatedTo,
    });
  }

  function getStep(position = 1) {
    const i = userNavigation.value.findIndex(
      ({ name }) => name === currentStep.value
    );
    return userNavigation.value[i + position];
  }

  function getSubRouteName(route: ToBeFixed) {
    return (route.name || '').split(':')[1];
  }

  // Go to tab.
  function userNavTo(value: string) {
    if (value == undefined) return;
    if (value == currentStep.value) value = ''; // toggle
    track('order:tab-changed', {
      comingFrom: currentStep.value,
      goingTo: value,
    });
    currentStep.value = value;
  }

  async function userNavToGuarded(value: string) {
    stepSubmitted.value = true;
    const isForward =
      currentStep.value !== '' &&
      isOrderTabGreaterThan(value, currentStep.value);
    if (orderStore.customerIsEditing && isForward && !nextButtonActive.value)
      return;
    userNavTo(value);
  }

  function updateValidation() {
    updateValidationForFiles();

    tncAccepted.value = validateFormByName('checkoutForm');
    stepValidation.review = validateFormByName('reviewForm');
    stepValidation.delivery = validateFormByName('deliveryForm');
    stepValidation.cutlist =
      !orderStore.isCutlist || validateFormByName('cutlistAppForm');
  }

  function validateFormByName(formName: string) {
    const ctx = useFormKitContextById(formName);
    if (!ctx.value) return true;

    return Boolean(ctx.value?.state.valid);
  }

  function updateValidationForFiles() {
    stepValidation.files =
      validateFormByName('uploadForm') && filesStore.filesForQuoting.length > 0;
  }

  async function submitCurrentForm() {
    const formName = currentTabConfig.value?.formName;
    if (!formName) return;

    return submitForm(formName);
  }

  // Advance to the next step, IF conditions for current step are met.
  async function nextStep() {
    stepSubmitted.value = true;
    if (currentTabs.value.includes('files')) {
      authController.authenticateUserIfNecessary(
        filesStore.filesForQuoting.length > 0,
        orderStore.cutlistId
      );
    }

    try {
      await submitCurrentForm();
    } catch (e) {
      console.error('Error submitting form:', e);
      return;
    }

    if (!nextButtonActive.value) return;

    if (orderStore.orderId) {
      await orderStore.saveOrder({
        reason: `Completed step ${currentStep.value}`,
        toast: { success: false },
      });
    }

    const nextStep = getStep(1);
    if (!nextStep) return;

    goToStep(nextStep.name);
  }

  function previousStep() {
    const prevStep = getStep(-1);
    track('page:order:user-went-back', { page: route.name });
    if (prevStep) {
      goToStep(prevStep.name);
    }
  }

  async function checkout() {
    updateValidation();
    if (!checkoutActive.value) return;

    if (!userStore.isLoggedIn) {
      return authController.showAuthenticateModal(
        {
          bodyText: undefined,
          headerTextKey: 'order:modals.authenticate.header.submitQuote',
        },
        checkout
      );
    }

    orderStore.currentOrder.orderstatus = 'quote_request';
    await orderStore
      .saveOrder({
        include: [...DEFAULT_ORDER_SAVE_PROPS, 'orderstatus'],
        reason: `Submitted by ${userStore.activeUser.email}`,
        toast: { success: false },
      })
      .catch(() => {
        modal.open('global', {
          header: 'Could not submit your request',
          body: `There was an problem submitting your request. Please get in touch via chat.`,
          button2label: 'Ok.',
        });
      });

    track('order:submit', orderStore.currentOrder);

    if (userStore.isOrderCustomer) {
      track('order:intake', { order: orderStore.currentOrder });
      orderSuccess.value = true;
    }

    localStorage.removeItem(lastValidStepStorageKey.value);
  }

  async function handleOrderLoadFail(response: AxiosResponse) {
    if (
      response.data?.reason === 'user_organization_mismatch' &&
      userStore.canSwitch
    ) {
      return modal.open('select-organisation-box');
    }

    const alreadyAccepted =
      response.status === 410 ||
      (response.status === 404 &&
        response.data?.reason === 'already_accepted_or_expired');

    if (alreadyAccepted) {
      return router.replace({
        name: 'workspace',
        query: { alreadyAccepted: 'true' },
      });
    }

    return router.push({ name: 'notfound' });
  }

  watchEffect(() => {
    if (currfentFormState?.value?.valid !== undefined) {
      stepValidation[currentStep.value as keyof typeof stepValidation] =
        currfentFormState.value.valid;
    }
  });

  return {
    reset,
    route,
    stepValidation,
    lastValidStep,
    currentStep,
    currentTabs,
    currentTabConfig,
    hasFilesManagement,
    navigation,
    userNavigation,
    getStep,
    goToStep,
    userNavTo,
    trackCurrentTab,
    updateStepURL,
    stepSubmitted,
    nextButtonActive,
    userNavToGuarded,
    hasManufacturingIssues,
    lastValidStepStorageKey,
    hasOwnViewer,
    autoSelectsDefaultFile,
    isPositionsOrder,
    isPaneExpanded,
    showNextButton,
    showCheckout,
    checkoutActive,
    hasDeliveryAddress,
    tncAccepted,
    orderSuccess,
    nextStep,
    previousStep,
    checkout,
    updateValidation,
    updateValidationForFiles,
    submitCurrentForm,
    validateFormByName,
    handleOrderLoadFail,
  };
};
